import { X } from 'lucide-react'
import { useState } from 'react'

import { Combobox } from '@/components/Combobox'
import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, DetailSection } from '@/components/data/DetailPane'
import { LoadingRows } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { useSelection } from '@/lib/useSelection'
import { plural } from '@/lib/utils'

import { SectionError, SectionStrip, TOKEN_HINT } from './SectionLabel'
import type { DialogState } from './types'

export type RoleActor = { type: 'user' | 'group'; id: string; name: string }
export type Role = { role_id: string; name: string; actors: RoleActor[] }

const names = (r: Role, type: RoleActor['type']) =>
  r.actors.filter((a) => a.type === type).map((a) => a.name).join(', ') || '—'

const COLUMNS: Column<Role>[] = [
  { key: 'name', header: 'Role', width: '22%' },
  { key: 'count', header: 'Members', mono: true, width: '96px', align: 'right', render: (r) => r.actors.length },
  { key: 'users', header: 'Users', render: (r) => names(r, 'user'), title: (r) => names(r, 'user') },
  { key: 'groups', header: 'Groups', mono: true, width: '30%', render: (r) => names(r, 'group'), title: (r) => names(r, 'group') },
]

function RoleDetail({ projectKey, role, refresh, onDialog }: {
  projectKey: string; role: Role; refresh: () => void; onDialog: (d: DialogState) => void
}) {
  const [mode, setMode] = useState<'user' | 'group'>('user')
  const [value, setValue] = useState('')
  return (
    <DetailPane eyebrow="Project role" title={role.name} status={<span className="text-text-2">{plural(role.actors.length, 'member')}</span>}>
      <DetailSection title="Members">
        {role.actors.length === 0
          ? <p className="text-text-3">Nobody in this role.</p>
          : (
            <ul className="divide-y divide-line-row">
              {role.actors.map((a) => (
                <li key={`${a.type}:${a.id}`} className="flex items-center gap-2 py-1">
                  <span className="w-12 shrink-0 text-[11px] text-text-3">{a.type}</span>
                  <span className={`min-w-0 flex-1 truncate ${a.type === 'group' ? 'font-mono text-[12px]' : ''}`} title={a.name}>
                    {a.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${a.name} from ${role.name}`}
                    title="Remove from role"
                    className="rounded p-1 text-text-2 transition-colors hover:bg-danger/10 hover:text-danger"
                    onClick={() => onDialog({
                      op: 'projects.remove_role_actors',
                      params: a.type === 'user'
                        ? { project_key: projectKey, role_id: role.role_id, user_account_ids: [a.id] }
                        : { project_key: projectKey, role_id: role.role_id, group_names: [a.id] },
                      title: `Remove ${a.name} from ${role.name}`,
                      onDone: refresh,
                    })}
                  >
                    <X aria-hidden className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
      </DetailSection>
      <DetailSection title="Add member">
        <div className="flex items-start gap-2">
          <div role="radiogroup" aria-label="Member type" className="flex shrink-0 overflow-hidden rounded-md border border-input">
            {(['user', 'group'] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                onClick={() => setMode(m)}
                className={`px-2.5 py-1 text-[12px] transition-colors ${mode === m ? 'bg-surface-active text-foreground' : 'text-text-2 hover:text-foreground'}`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="min-w-0 flex-1">
            <Combobox
              key={mode}
              source={mode}
              valueKey={mode === 'group' ? 'name' : 'account_id'}
              deps={{}}
              multi={false}
              value={value}
              onChange={setValue}
              ariaLabel={`Add ${mode} to ${role.name}`}
              className=""
              onSelect={(picked) => {
                if (!picked) return
                onDialog({
                  op: 'projects.add_role_actors',
                  params: mode === 'group'
                    ? { project_key: projectKey, role_id: role.role_id, group_names: [picked] }
                    : { project_key: projectKey, role_id: role.role_id, user_account_ids: [picked] },
                  title: `Add to ${role.name}`,
                  onDone: () => {
                    setValue('')
                    refresh()
                  },
                })
              }}
            />
          </div>
        </div>
      </DetailSection>
    </DetailPane>
  )
}

export function RolesSection({ projectKey, roles, err, refresh, onDialog }: {
  projectKey: string
  roles: Role[] | null
  err: boolean
  refresh: () => void
  onDialog: (d: DialogState) => void
}) {
  const [sel, setSel] = useSelection()
  const assigned = (roles ?? []).filter((r) => r.actors.length > 0).length
  const selected = roles?.find((r) => r.role_id === sel) ?? null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SectionStrip label="roles" onRefresh={refresh}>
        {roles && (
          <span className="font-mono text-[11px] text-text-3">
            {assigned} of {plural(roles.length, 'role')} have members
          </span>
        )}
      </SectionStrip>
      {err ? <SectionError>Couldn’t load roles — {TOKEN_HINT}</SectionError>
        : roles === null ? <LoadingRows label="Loading roles" rows={4} />
        : (
          <SplitView
            onCloseDetail={() => setSel(null)}
            detail={selected && (
              <RoleDetail key={selected.role_id} projectKey={projectKey} role={selected} refresh={refresh} onDialog={onDialog} />
            )}
            list={(
              <DataTable label="Project roles" rows={roles} columns={COLUMNS} rowId={(r) => r.role_id}
                selectedId={sel} onSelect={setSel} empty="No roles." />
            )}
          />
        )}
    </div>
  )
}
